import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NzCardComponent} from "ng-zorro-antd/card";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {NzFormControlComponent, NzFormItemComponent, NzFormLabelComponent} from "ng-zorro-antd/form";
import {
  NzInputDirective,
  NzInputPasswordDirective,
  NzInputPrefixDirective,
  NzInputWrapperComponent
} from "ng-zorro-antd/input";
import {NzIconDirective} from "ng-zorro-antd/icon";
import {AuthenticateResponse, AuthFlowService} from "../../core/services/auth-flow.service";
import {NzButtonComponent} from "ng-zorro-antd/button";
import {finalize} from "rxjs";
import {NzNotificationService} from "ng-zorro-antd/notification";
import {AuthApiService} from "../../core/services/auth-api.service";
import {ActivatedRoute, Router} from "@angular/router";
import {NzModalModule, NzModalService} from "ng-zorro-antd/modal";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {textControl} from '../../shared/text-control';
import {ConsentModal} from "../login/consent-modal/consent-modal";
import {ExpiredPsdModal, ExpiredPsdModalData} from "../login/expired-psd-modal/expired-psd-modal";

@Component({
  selector: 'app-login-test',
  imports: [
    NzCardComponent,
    ReactiveFormsModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzInputDirective,
    NzInputWrapperComponent,
    NzIconDirective,
    NzInputPrefixDirective,
    NzInputPasswordDirective,
    NzButtonComponent,
    NzModalModule,
    TranslatePipe,
  ],
  templateUrl: './login-test.html',
  styleUrl: '../login/login.css',
})
export class LoginTest implements OnInit {
  private static safeLocalUrl(url: string | null | undefined): string | null {
    return url && /^\/(?![\/\\])/.test(url) ? url : null;
  }


  private readonly _authFlowService = inject(AuthFlowService);
  private readonly _notification = inject(NzNotificationService);
  private readonly _authApiService = inject(AuthApiService);
  protected readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _modal = inject(NzModalService);
  private readonly _translate = inject(TranslateService);

  visible = false;
  isAuthenticating = false;

  form: FormGroup = new FormGroup({
    login: new FormControl(null, [
      Validators.required,
      Validators.minLength(12),
      Validators.maxLength(12)
    ]),
    password: textControl([]),
  });

  ngOnInit(): void {
    const hasClientId = this._route.snapshot.queryParamMap.has('client_id');
    const origin = this._route.snapshot.queryParamMap.get('origin');
    const oauthReturnUrl = this._route.snapshot.queryParamMap.get('ReturnUrl');

    if (!hasClientId && !origin && !oauthReturnUrl) {
      return;
    }

    this._authApiService.loadSession().subscribe((authed) => {
      if (!authed) return;

      const safeReturn = LoginTest.safeLocalUrl(oauthReturnUrl);
      if (safeReturn) {
        window.location.href = safeReturn;
        return;
      }

      this._authFlowService.availableSystems().subscribe((systems) => {
        if (hasClientId && this.tryContinueOAuthFlow({systems} as AuthenticateResponse)) {
          return;
        }
        if (origin) {
          this._router.navigate(['/main'], { state: { systems }, queryParams: { origin } });
        }
      });
    });
  }

  tryAuthenticate(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isAuthenticating = true;

    this._authFlowService.authenticateTest({
      login: this.form.value.login,
      psd: this.form.value.password
    }).pipe(
      finalize(() => this.isAuthenticating = false),
    ).subscribe((res) => {
      if (res.psdExpired) {
        this.openExpiredPsdModal(res.psdResetToken, res.psdTemporary === true);
        return;
      }

      if (res.consentRequired) {
        this.openConsentModal(res.consentToken);
        return;
      }

      if (res.success === false) {
        this._notification.error("Ошибка", "Не удалось завершить вход");
        return;
      }

      this._authApiService.refreshSession().subscribe(() => {
        const safeReturn = LoginTest.safeLocalUrl(this._route.snapshot.queryParamMap.get('ReturnUrl'));
        if (safeReturn) {
          window.location.href = safeReturn;
          return;
        }

        if (this.tryContinueOAuthFlow(res)) {
          return;
        }

        const redirectUri = this._route.snapshot.queryParamMap.get('redirect_uri')
          ?? this._route.snapshot.queryParamMap.get('returnUrl');
        const origin = this._route.snapshot.queryParamMap.get('origin');

        const queryParams: Record<string, string> = {};
        if (redirectUri) queryParams['redirect_uri'] = redirectUri;
        if (origin) queryParams['origin'] = origin;

        this._router.navigate(['/main'], {
          state: { systems: res.systems },
          queryParams: Object.keys(queryParams).length ? queryParams : undefined
        });
      });
    });
  }

  private openConsentModal(consentToken?: string): void {
    const modalRef = this._modal.create<ConsentModal, {consentToken?: string}>({
      nzTitle: this._translate.instant('consent.title'),
      nzContent: ConsentModal,
      nzData: {consentToken},
      nzOkText: this._translate.instant('consent.submit'),
      nzCancelText: this._translate.instant('common.cancel'),
      nzOkDisabled: true,
      nzMaskClosable: false,
      nzWidth: 960,
      nzOnOk: (instance) => {
        instance.handleOk();
        return false;
      },
    });

    modalRef.afterClose
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((accepted?: boolean) => {
        if (accepted) {
          this.tryAuthenticate();
        }
      });
  }

  private openExpiredPsdModal(token: string | undefined, temporary: boolean): void {
    if (!token) {
      this._notification.error(
        this._translate.instant('expiredPsd.errorTitle'),
        this._translate.instant('expiredPsd.noToken')
      );
      return;
    }

    const modalRef = this._modal.create<ExpiredPsdModal, ExpiredPsdModalData>({
      nzTitle: this._translate.instant(temporary ? 'expiredPsd.tempTitle' : 'expiredPsd.title'),
      nzContent: ExpiredPsdModal,
      nzData: {token, temporary},
      nzOkText: this._translate.instant('expiredPsd.submit'),
      nzCancelText: this._translate.instant('common.cancel'),
      nzOkDisabled: true,
      nzMaskClosable: false,
      nzOnOk: (instance) => {
        instance.handleOk();
        return false;
      },
    });

    modalRef.afterClose
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((newPsd?: string) => {
        if (!newPsd) {
          return;
        }

        this.form.get('password')?.setValue(newPsd);
        this.tryAuthenticate();
      });
  }

  private tryContinueOAuthFlow(res: AuthenticateResponse): boolean {
    const clientId = this._route.snapshot.queryParamMap.get('client_id');
    const redirectUri = this._route.snapshot.queryParamMap.get('redirect_uri')
      ?? this._route.snapshot.queryParamMap.get('returnUrl');

    if (!clientId || !redirectUri) {
      return false;
    }

    const system = res.systems.find((item) =>
      item.audience?.toLowerCase() === clientId.toLowerCase() ||
      item.clientId?.toLowerCase() === clientId.toLowerCase());

    if (!system) {
      this._notification.error(
        "Ошибка",
        "Модуль для OAuth входа не найден"
      );
      return true;
    }

    const params = new URLSearchParams();
    params.set('response_type', 'code');
    params.set('client_id', clientId);
    params.set('redirect_uri', redirectUri);
    params.set('scope', this._route.snapshot.queryParamMap.get('scope') ?? 'openid profile offline_access');

    const state = this._route.snapshot.queryParamMap.get('state');
    if (state) params.set('state', state);
    const nonce = this._route.snapshot.queryParamMap.get('nonce');
    if (nonce) params.set('nonce', nonce);
    const codeChallenge = this._route.snapshot.queryParamMap.get('code_challenge');
    if (codeChallenge) {
      params.set('code_challenge', codeChallenge);
      params.set('code_challenge_method', this._route.snapshot.queryParamMap.get('code_challenge_method') ?? 'S256');
    }

    window.location.href = `/api/sso/connect/authorize?${params.toString()}`;
    return true;
  }
}
